// src/components/categories/CategoriesTable.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getAllAdherentsListWithGroup } from "./../../../../services/adherentServices";
import SimpleModal from "./SimpleModal";
import FiltersBar from "./FiltersBar";
import {
  buildCategoriesForSeason,
  getAgeAtEndOfSeason,
  getAgeCategoryKey,
  getCurrentSeasonStartYear,
  seasonStringFromStartYear,
  weightCategories,
  isMorpho,
} from "./../utils/judoCategories";

const toAbsolute = (u) => {
  if (!u) return "";
  const s = String(u).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:"))
    return s;
  if (!s.startsWith("/uploads")) return s;
  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${s}`;
};

const parseNumKg = (label) => {
  const m = String(label).match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
};

const CategoriesTable = () => {
  // --- Filtres/état
  const defaultSeason = useMemo(
    () => seasonStringFromStartYear(getCurrentSeasonStartYear()),
    []
  );
  const [season, setSeason] = useState(defaultSeason);
  const [selectedGenre, setSelectedGenre] = useState("Tous");
  const [selectedAnnee, setSelectedAnnee] = useState("Tous");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [groupOptions, setGroupOptions] = useState([]);

  // Données calculées
  const [counts, setCounts] = useState({}); // key: `${ageKey}|${genre}|${bracket}` → number
  const [members, setMembers] = useState({}); // même clé → array adherents
  const [loadingCounts, setLoadingCounts] = useState(false);

  // Modale
  const [modalOpen, setModalOpen] = useState(false);
  const [modalList, setModalList] = useState([]);
  const [modalTitle, setModalTitle] = useState("");

  const categories = useMemo(() => buildCategoriesForSeason(season), [season]);

  const anneesOptions = useMemo(
    () =>
      Array.from({ length: 110 }, (_, i) =>
        (new Date().getFullYear() - i).toString()
      ),
    []
  );

  const seasonOptions = useMemo(() => {
    const s = getCurrentSeasonStartYear();
    return [s - 1, s, s + 1, s + 2].map(seasonStringFromStartYear);
  }, []);

  // --- Chargement + agrégation
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setLoadingCounts(true);
        const data = await getAllAdherentsListWithGroup();
        if (cancelled) return;

        // Build options groupes
        const setNames = new Set();
        (Array.isArray(data) ? data : []).forEach((a) =>
          (a?.Groups || []).forEach((g) => g?.nom && setNames.add(g.nom))
        );
        setGroupOptions([...setNames].sort((a, b) => a.localeCompare(b, "fr")));

        // Bornage / filtres initiaux
        const filteredBase = (data || []).filter((a) => {
          const poids = Number(a.poids);
          const seasonOk = a?.saison_active === season;
          const genreOk = a?.genre === "Masculin" || a?.genre === "Féminin";
          const dobOk = !!a?.date_naissance;
          const weightOk = Number.isFinite(poids) && poids > 0;
          const groupOk =
            !selectedGroup ||
            (a?.Groups || []).some((g) => g?.nom === selectedGroup);
          return seasonOk && genreOk && dobOk && weightOk && groupOk;
        });

        // Agrégation
        const nextCounts = {};
        const nextMembers = {};

        // Importé tard pour éviter cycles (si vous préférez, déplacez le helper ici)
        const { getWeightBracket } = await import("./../utils/judoCategories");

        for (const a of filteredBase) {
          const age = getAgeAtEndOfSeason(a.date_naissance, season);
          const ageKey = getAgeCategoryKey(age, a.genre);
          if (!ageKey) continue;
          const bracket = getWeightBracket(ageKey, Number(a.poids));
          if (!bracket) continue;

          const key = `${ageKey}|${a.genre}|${bracket}`;
          nextCounts[key] = (nextCounts[key] || 0) + 1;
          (nextMembers[key] = nextMembers[key] || []).push(a);
        }

        if (!cancelled) {
          setCounts(nextCounts);
          setMembers(nextMembers);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoadingCounts(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [season, selectedGroup]);

  // --- Helpers d’affichage
  const formatName = (a) =>
    `${(a.prenom || "").trim()} ${(a.nom || "").trim()}`.trim();
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("T")[0].split("-");
    return `${d}-${m}-${y.slice(-2)}`;
  };

  const openModalFor = (ageCategory, genre, bracket, humanLabel) => {
    const key = `${ageCategory}|${genre}|${bracket}`;
    const list = members[key] || [];
    setModalTitle(`${humanLabel} — ${genre} — ${bracket} (${list.length})`);
    setModalList(
      list.slice().sort((a, b) => formatName(a).localeCompare(formatName(b)))
    );
    setModalOpen(true);
  };

  // --- Filtrage "visuel" (genre + année)
  const filteredRows = useMemo(() => {
    return categories
      .filter((item) => {
        const matchesGenre =
          selectedGenre === "Tous" || item.genre === selectedGenre;
        if (selectedAnnee === "Tous") return matchesGenre;

        const year = parseInt(selectedAnnee, 10);
        if (item.annees.includes(" - ")) {
          const [a, b] = item.annees
            .split(" - ")
            .map((s) => parseInt(s.trim(), 10));
          return matchesGenre && year >= a && year <= b;
        }
        if (item.annees.includes("et avant")) {
          const fromYear = parseInt(item.annees, 10);
          return matchesGenre && year <= fromYear;
        }
        return matchesGenre;
      })
      .map((item) => {
        const list = weightCategories[item.ageCategory] || ["Non défini"];
        const weightCategoryString = list.includes("Groupes Morphologiques")
          ? "Groupes Morphologiques (par poids réel)"
          : list.join(", ");
        return { ...item, weightCategories: weightCategoryString };
      });
  }, [categories, selectedGenre, selectedAnnee]);

  // --- Brackets à afficher par ligne :
  // - Si "officiel": on prend la liste statique.
  // - Si "morpho": on dérive dynamiquement des clés présentes dans `counts` (pour éviter des centaines de cases vides).
  const bracketsForRow = (ageCategory, genre) => {
    const list = weightCategories[ageCategory] || [];
    if (!list.includes("Groupes Morphologiques")) return list;

    // dériver depuis counts: `${ageKey}|${genre}|${bracket}`
    const prefix = `${ageCategory}|${genre}|`;
    const seen = [];
    for (const k of Object.keys(counts)) {
      if (k.startsWith(prefix)) {
        const label = k.slice(prefix.length);
        seen.push(label);
      }
    }
    // tri numérique par kg (23 kg < 24 kg), fallback alpha
    return seen.sort((a, b) => {
      const na = parseNumKg(a);
      const nb = parseNumKg(b);
      if (na != null && nb != null) return na - nb;
      return String(a).localeCompare(String(b), "fr");
    });
  };

  return (
    <div className="container mx-auto my-8">
      <FiltersBar
        season={season}
        setSeason={setSeason}
        seasonOptions={seasonOptions}
        selectedGenre={selectedGenre}
        setSelectedGenre={setSelectedGenre}
        selectedAnnee={selectedAnnee}
        setSelectedAnnee={setSelectedAnnee}
        anneesOptions={anneesOptions}
        selectedGroup={selectedGroup}
        setSelectedGroup={setSelectedGroup}
        groupOptions={groupOptions}
      />

      {loadingCounts && (
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-3">
          Calcul des compteurs…
        </p>
      )}

      <div className="relative -mx-2 sm:mx-0">
        <div
          className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700"
          role="region"
          aria-label="Tableau des catégories"
        >
          <table className="min-w-[920px] w-full bg-white dark:bg-gray-800">
            <thead className="bg-gray-700 sticky top-0 z-10">
              <tr>
                <th className="py-3 px-4 text-left text-xs sm:text-sm font-medium text-gray-200 uppercase sticky left-0 bg-gray-700">
                  Catégorie d'Âge
                </th>
                <th className="py-3 px-4 text-left text-xs sm:text-sm font-medium text-gray-200 uppercase">
                  Genre
                </th>
                <th className="py-3 px-4 text-left text-xs sm:text-sm font-medium text-gray-200 uppercase">
                  Années de Naissance
                </th>
                <th className="py-3 px-4 text-left text-xs sm:text-sm font-medium text-gray-200 uppercase">
                  Catégories de Poids
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((row, idx) => {
                const rowBrackets = bracketsForRow(row.ageCategory, row.genre);
                const isMorphoRow = isMorpho(row.ageCategory);

                return (
                  <tr
                    key={`${row.label}-${row.genre}-${idx}`}
                    className={`border-b dark:border-gray-700 ${
                      row.genre === "Masculin" ? "bg-blue-50" : "bg-pink-50"
                    }`}
                  >
                    <td className="py-3 px-4 text-xs sm:text-sm text-gray-800 border-b border-gray-200 dark:border-gray-700 sticky left-0 bg-inherit whitespace-nowrap">
                      {row.label}
                    </td>
                    <td className="py-3 px-4 text-xs sm:text-sm text-gray-800 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap">
                      {row.genre}
                    </td>
                    <td className="py-3 px-4 text-xs sm:text-sm text-gray-800 border-b border-gray-200 dark:border-gray-700 whitespace-nowrap">
                      {row.annees}
                    </td>
                    <td className="py-3 px-4 text-xs sm:text-sm text-gray-800 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex flex-wrap gap-2">
                        {rowBrackets.length === 0 ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full border text-[11px] sm:text-xs bg-gray-100 border-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600">
                            {isMorphoRow ? "Aucun poids saisi" : "Non défini"}
                          </span>
                        ) : (
                          rowBrackets.map((w) => {
                            const key = `${row.ageCategory}|${row.genre}|${w}`;
                            const c = counts[key] || 0;
                            const has = c > 0;

                            if (has) {
                              return (
                                <button
                                  type="button"
                                  key={w}
                                  onClick={() =>
                                    openModalFor(
                                      row.ageCategory,
                                      row.genre,
                                      w,
                                      row.label
                                    )
                                  }
                                  className="relative inline-flex items-center px-2 py-1 rounded-full border text-[11px] sm:text-xs bg-emerald-200 border-emerald-400 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700 hover:brightness-95 whitespace-nowrap"
                                  title={`${c} judoka(s)`}
                                >
                                  {w}
                                  <span className="ml-2 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full text-[10px] font-bold bg-emerald-500 text-white dark:bg-emerald-600">
                                    {c}
                                  </span>
                                </button>
                              );
                            }

                            return (
                              <span
                                key={w}
                                className="relative inline-flex items-center px-2 py-1 rounded-full border text-[11px] sm:text-xs bg-gray-100 border-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600 whitespace-nowrap"
                                title="Aucun"
                              >
                                {w}
                              </span>
                            );
                          })
                        )}
                      </div>
                      {isMorphoRow && (
                        <p className="mt-2 text-[11px] text-gray-500">
                          Regroupement dynamique par <strong>poids réel</strong>{" "}
                          (ex: “23 kg”). Ajoutez/éditez le poids d’un adhérent
                          pour qu’il apparaisse ici.
                        </p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <SimpleModal
        open={modalOpen}
        title={modalTitle}
        onClose={() => setModalOpen(false)}
      >
        {modalList.length === 0 ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Aucun judoka dans cette catégorie.
          </p>
        ) : (
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {modalList.map((a) => (
              <li key={a.id} className="py-2 flex items-center gap-3">
                {a.photo_url ? (
                  <img
                    src={toAbsolute(a.photo_url)}
                    alt={formatName(a)}
                    className="w-10 h-10 rounded-full object-cover border border-gray-200 dark:border-gray-700"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700" />
                )}
                <div className="flex-1">
                  <div className="font-medium">
                    {formatName(a)}{" "}
                    <span className="text-xs text-gray-500">({a.genre})</span>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300">
                    Né(e) le {formatDate(a.date_naissance)} • {Number(a.poids)}{" "}
                    kg
                  </div>
                  {Array.isArray(a.Groups) && a.Groups.length > 0 && (
                    <div className="text-xs text-gray-500">
                      Groupes :{" "}
                      {a.Groups.map((g) => g?.nom)
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </SimpleModal>
    </div>
  );
};

export default CategoriesTable;
